import { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Loader2, RefreshCw, Users, Star, Shuffle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { fetchWithTimeout, classifyError } from '@/lib/fetchWithTimeout';
import type { SubmissionResult } from '@/pages/Onboarding';
import { ScoreBreakdown } from '@/components/results/ScoreBreakdown';
import { PointsEarned } from '@/components/results/PointsEarned';
import { OriginalPromptCard } from '@/components/results/OriginalPromptCard';
import { ImprovedVersionCard } from '@/components/results/ImprovedVersionCard';
import QuickWinsCard from '@/components/QuickWinsCard';
import PromptBuilderChat from '@/components/prompt-builder/PromptBuilderChat';
import { BuilderProgressSidebar } from '@/components/prompt-builder/BuilderProgressSidebar';
import type { BuilderMessage } from '@/lib/promptBuilderUtils';
import { WhyChangesSection } from './WhyChangesSection';
import type { RemixContext } from '@/types/promptBuilder';

import { GapScoreCard } from './GapScoreCard';
import { OnboardingOverviewCard } from './OnboardingOverviewCard';
import { OnboardingFeatureCards } from './OnboardingFeatureCards';

interface OnboardingPromptBuilderProps {
  stepNumber?: number;
  initialGoal?: string;
  prefillInput?: string;
  deferInitialSend?: boolean;
  remixContext?: RemixContext & { originalScore?: number };
  onScored: (result: SubmissionResult) => void;
  onPhaseChange?: (phase: 'input' | 'results') => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  /** When true, skip the full results view and call onScored immediately when score is ready */
  skipResultsView?: boolean;
}

interface PointsData {
  base: number;
  bonus: number;
  rubric: number;
  original_rubric?: number;
  total: number;
  is_chat: boolean;
}

interface Bubble {
  id: string;
  rubric_driver: string;
  title: string;
  body: string;
  anchor_hint: string;
}

interface ScoreResult {
  overall_score: number;
  original_score: number;
  evaluation_markdown: string;
  drift_flag: string | null;
  points: PointsData;
  submitted_content: string;
  dimension_scores?: Record<string, { score: number; rationale: string }>;
  chatcraft_subscores?: Record<string, number>;
  improvements?: string[];
  rewrite_tagged?: string;
  rewrite_clean?: string;
  bubbles?: Bubble[];
}

export function OnboardingPromptBuilder({
  stepNumber = 1,
  initialGoal,
  prefillInput,
  deferInitialSend = false,
  remixContext,
  onScored,
  onPhaseChange,
  isLoading,
  setIsLoading,
  skipResultsView = false,
}: OnboardingPromptBuilderProps) {
  const { user } = useAuth();
  const [showBuilder, setShowBuilder] = useState(true);
  const [scoreResult, setScoreResult] = useState<ScoreResult | null>(null);
  const [failedPrompt, setFailedPrompt] = useState<{ prompt: string; conversationId?: string | null } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [chatMessages, setChatMessages] = useState<BuilderMessage[]>([]);

  const handleStepChange = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const handleMessagesChange = useCallback((messages: BuilderMessage[]) => {
    setChatMessages(messages);
  }, []);

  const handleBuilderComplete = async (assembledPrompt: string, conversationId?: string | null) => {
    if (!user) return;
    
    setIsLoading(true);
    setErrorMessage(null);
    setFailedPrompt(null);
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const response = await fetchWithTimeout(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/score-submission`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionData.session?.access_token}`,
          },
          body: JSON.stringify({
            content: assembledPrompt,
            submission_type: 'prompt_single',
            source: 'onboarding',
            conversation_id: conversationId || undefined,
          }),
        },
        300_000,
      );

      const data = await response.json();

      if (!response.ok) {
        const classified = classifyError(null, response.status);
        if (classified.retryable) {
          setFailedPrompt({ prompt: assembledPrompt, conversationId });
          setErrorMessage(classified.message);
          setIsLoading(false);
          return;
        }
        throw new Error(data.error || classified.message);
      }

      const points: PointsData = data.points || {
        base: 5,
        bonus: 0,
        rubric: data.overall_score,
        original_rubric: data.original_score,
        total: 5 + data.overall_score,
        is_chat: false,
      };

      const dimensionScores = data.analysis?.dimension_scores || {};
      const chatcraftScores = data.analysis?.chatcraft_scores || null;
      const originalScore = data.original_score ?? Math.round(data.overall_score * 0.6);

      if (remixContext?.originalScoreId) {
        const { error: remixError } = await supabase.from('prompt_remixes').insert({
          original_score_id: remixContext.originalScoreId,
          remixed_score_id: data.score_id,
          user_id: user.id,
        });
        if (remixError) {
          console.error('Failed to record remix:', remixError);
        }
      }

      if (skipResultsView) {
        onScored({
          id: data.score_id || crypto.randomUUID(),
          overall_score: data.overall_score,
          original_score: remixContext?.originalScore ?? originalScore,
          evaluation_markdown: JSON.stringify(data.analysis),
          drift_flag: data.drift_flag,
          exercise_type: 'build',
          original_content: assembledPrompt,
          submission_type: 'prompt_single',
          dimension_scores_json: dimensionScores,
          points_base: points.base,
          points_bonus: points.bonus,
          points_rubric: points.rubric,
          points_total: points.total,
          improvements: data.analysis?.improvements || [],
          rewrite_tagged: data.analysis?.rewrite_tagged || '',
          rewrite_clean: data.analysis?.rewrite_clean || '',
          bubbles: data.analysis?.bubbles || [],
        });
        return;
      }

      setScoreResult({
        overall_score: data.overall_score,
        original_score: originalScore,
        evaluation_markdown: JSON.stringify(data.analysis),
        drift_flag: data.drift_flag,
        points,
        submitted_content: assembledPrompt,
        dimension_scores: dimensionScores,
        chatcraft_subscores: chatcraftScores,
        improvements: data.analysis?.improvements || [],
        rewrite_tagged: data.analysis?.rewrite_tagged || '',
        rewrite_clean: data.analysis?.rewrite_clean || '',
        bubbles: data.analysis?.bubbles || [],
      });

      setShowBuilder(false);
      onPhaseChange?.('results');
    } catch (err) {
      console.error('Submission error:', err);
      const classified = classifyError(err);
      if (classified.retryable) {
        setFailedPrompt({ prompt: assembledPrompt, conversationId });
        setErrorMessage(classified.message);
      } else {
        toast({
          title: 'Error',
          description: classified.message,
          variant: 'destructive',
        });
      }
    } finally {
      if (!skipResultsView) {
        setIsLoading(false);
      }
    }
  };

  const handleRetryScoring = () => {
    if (failedPrompt) {
      handleBuilderComplete(failedPrompt.prompt, failedPrompt.conversationId);
    }
  };

  const handleContinue = () => {
    if (scoreResult) {
      onScored({
        id: crypto.randomUUID(),
        overall_score: scoreResult.overall_score,
        original_score: remixContext?.originalScore ?? scoreResult.original_score,
        evaluation_markdown: scoreResult.evaluation_markdown,
        drift_flag: scoreResult.drift_flag,
        exercise_type: 'build',
        original_content: scoreResult.submitted_content,
        submission_type: 'prompt_single',
        dimension_scores_json: scoreResult.dimension_scores,
        points_base: scoreResult.points.base,
        points_bonus: scoreResult.points.bonus,
        points_rubric: scoreResult.points.rubric,
        points_total: scoreResult.points.total,
        improvements: scoreResult.improvements,
        rewrite_tagged: scoreResult.rewrite_tagged,
        rewrite_clean: scoreResult.rewrite_clean,
        bubbles: scoreResult.bubbles,
      });
    }
  };

  const gap = scoreResult ? scoreResult.overall_score - scoreResult.original_score : 0;

  // Show results after scoring - Step 3 gets FSR Explainer + Gap Score
  if (scoreResult && !showBuilder) {
    return (
      <div className="space-y-4">

        {/* Header - 2/3 width centered */}
        <div className="max-w-[66%] mx-auto">
          <Card className="border-primary/20 bg-card/80 backdrop-blur">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-success" />
                Submission {stepNumber} Scored!
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Overview Card - full width */}
        <OnboardingOverviewCard
          icon={Users}
          title="Featuring and Remixing Prompts"
          body="Building prompt libraries is hard, and making someone else's prompt work for you is harder. Favorites and Remix solve both — save the best prompts and personalize them with Cosmo's guided builder."
        />

        {/* Feature Cards - 2 across */}
        <OnboardingFeatureCards features={[
          { icon: Star, title: 'Favorites', description: 'Save any prompt to your library by clicking the star. Access saved prompts anytime from your Library.' },
          { icon: Shuffle, title: 'Remix', description: 'Click Remix to personalize any prompt. Cosmo asks similar questions as the builder. Available on any prompt page or from the Feed.' },
        ]} />

        {/* Two-column results - matches production Prompt Score page */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            <OriginalPromptCard originalText={scoreResult.submitted_content} />

            {scoreResult.rewrite_tagged && scoreResult.rewrite_clean && (
              <ImprovedVersionCard
                rewriteTagged={scoreResult.rewrite_tagged}
                rewriteClean={scoreResult.rewrite_clean}
                improvements={scoreResult.improvements}
              />
            )}

            {scoreResult.bubbles && scoreResult.bubbles.length > 0 && (
              <WhyChangesSection bubbles={scoreResult.bubbles} />
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            <PointsEarned
              pointsBase={scoreResult.points.base}
              originalScore={scoreResult.original_score}
              improvedScore={scoreResult.overall_score}
              pointsBonus={scoreResult.points.bonus}
              isChat={scoreResult.points.is_chat}
              improvements={scoreResult.improvements}
            />

            <GapScoreCard gap={gap} />

            <ScoreBreakdown
              overallScore={scoreResult.overall_score}
              evaluationMarkdown={scoreResult.evaluation_markdown}
              dimensionScores={scoreResult.dimension_scores}
              chatcraftScoresJson={scoreResult.chatcraft_subscores}
              isChat={false}
            />

            {scoreResult.improvements && scoreResult.improvements.length > 0 && (
              <QuickWinsCard wins={scoreResult.improvements} />
            )}
          </div>
        </div>

        {/* Bottom contextual message */}
        <Card className="border-success/20 bg-success/5">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              By using Cosmo's "Build a Prompt", you were able to add more context which improves 
              AI outputs. This reduces the gap between how you write prompts and how much the AI 
              has to help. The less help the more likely your prompt will work so keep closing that gap!
            </p>
          </CardContent>
        </Card>

        <Button onClick={handleContinue} className="w-full" size="lg">
          See Your Results
        </Button>
      </div>
    );
  }

  // Show builder interface
  return (
    <Card className="border-primary/20 bg-card/80 backdrop-blur overflow-hidden flex flex-col h-[75vh] min-h-[550px] md:min-h-[650px]">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline">Build a Prompt</Badge>
        </div>
        <CardTitle>Build a Prompt with Cosmo</CardTitle>
        <CardDescription>
          Let's build a prompt together! I'll guide you through our 4-part framework to create a high-quality prompt.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0 flex-1 min-h-0 flex">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 flex-1">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Scoring your prompt...</span>
          </div>
        ) : errorMessage && failedPrompt ? (
          <div className="flex flex-col items-center justify-center py-12 flex-1 gap-3 px-6">
            <p className="text-sm text-destructive text-center">{errorMessage}</p>
            <Button variant="outline" size="sm" onClick={handleRetryScoring}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Retry Scoring
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0 flex flex-col min-w-0">
              <PromptBuilderChat
                initialGoal={initialGoal}
                prefillInput={prefillInput}
                deferInitialSend={deferInitialSend}
                remixContext={remixContext}
                onComplete={handleBuilderComplete}
                onClose={() => {}} // No-op for inline onboarding
                inline={true}
                guided={false}
                onStepChange={handleStepChange}
                onMessagesChange={handleMessagesChange}
              />
            </div>
            <aside className="hidden lg:block w-60 border-l border-border flex-shrink-0 min-h-0 overflow-y-auto">
              <BuilderProgressSidebar
                currentStep={currentStep}
                messages={chatMessages}
                goal={initialGoal}
              />
            </aside>
          </>
        )}
      </CardContent>
    </Card>
  );
}
